import { createServiceSupabaseClient } from "@/lib/api-supabase";
import { categoryLabels } from "@/lib/categoryLabels";
import {
  categoryStructure,
  subcategories,
  type CategoryStructure,
  type SubCategory,
} from "@/lib/categoryStructure";
import { sanitizeNearMeSubcategories } from "@/lib/push-notifications/near-me-category-filter";

export type CategoryActivationMaps = {
  categories: Record<string, boolean>;
  subcategories: Record<string, boolean>;
};

export type CategoryActivationItemCounts = {
  byCategory: Record<string, number>;
  bySubcategory: Record<string, number>;
};

export type CategoryActivationState = CategoryActivationMaps & {
  itemCounts?: CategoryActivationItemCounts;
};

export type ActiveCategoryIds = {
  categories: string[];
  subcategories: string[];
};

type ActivationRow = {
  entity_type: string;
  entity_id: string;
  is_active: boolean | null;
};

const SUBCATEGORY_PARENT = new Map(
  subcategories.map((sub) => [sub.id, sub.parentCategory] as const),
);

export function getAllCategoryAndSubcategoryIds(): {
  categoryIds: string[];
  subcategoryIds: string[];
} {
  const categoryIds = Object.keys(categoryLabels) as string[];
  const subcategoryIds = subcategories.map((s) => s.id);
  return { categoryIds, subcategoryIds };
}

/** Default maps: every known category and subcategory enabled. */
export function createDefaultCategoryActivationMaps(): CategoryActivationMaps {
  const { categoryIds, subcategoryIds } = getAllCategoryAndSubcategoryIds();
  const categories: Record<string, boolean> = {};
  const subcategoryMap: Record<string, boolean> = {};
  for (const id of categoryIds) categories[id] = true;
  for (const id of subcategoryIds) subcategoryMap[id] = true;
  return { categories, subcategories: subcategoryMap };
}

/** Build admin activation maps from `category_activation_settings` rows. */
export function buildCategoryActivationMapsFromRows(
  rows: ActivationRow[] | null | undefined,
): CategoryActivationMaps {
  const maps = createDefaultCategoryActivationMaps();
  if (!rows?.length) return maps;

  for (const row of rows) {
    if (row.is_active !== false) continue;
    if (row.entity_type === "category") {
      maps.categories[row.entity_id] = false;
    } else if (row.entity_type === "subcategory") {
      maps.subcategories[row.entity_id] = false;
    }
  }
  return maps;
}

/**
 * Fill missing ids (default enabled) and cascade: category off ⇒ all its
 * subcategories off. Used by admin PUT before preflight + persist.
 */
export function normalizeCategoryActivationMaps(
  partial: Partial<CategoryActivationMaps> | null | undefined,
): CategoryActivationMaps {
  const maps = createDefaultCategoryActivationMaps();
  const incomingCategories = partial?.categories ?? {};
  const incomingSubcategories = partial?.subcategories ?? {};

  for (const id of Object.keys(maps.categories)) {
    if (incomingCategories[id] === false) maps.categories[id] = false;
  }
  for (const id of Object.keys(maps.subcategories)) {
    if (incomingSubcategories[id] === false) maps.subcategories[id] = false;
  }

  for (const [categoryId, active] of Object.entries(maps.categories)) {
    if (active !== false) continue;
    const cat = categoryStructure.find((c) => c.id === categoryId);
    if (!cat) continue;
    for (const sub of cat.subcategories) {
      maps.subcategories[sub.id] = false;
    }
  }

  return maps;
}

export type CategoryActivationDisabledRow = {
  entity_type: "category" | "subcategory";
  entity_id: string;
  is_active: false;
};

/** Diff desired vs current maps into batched enable (delete) / disable (upsert) ops. */
export type CategoryActivationWritePlan = {
  categoryIdsToEnable: string[];
  subcategoryIdsToEnable: string[];
  disabledRowsToUpsert: CategoryActivationDisabledRow[];
};

export function buildCategoryActivationWritePlan(
  desired: CategoryActivationMaps,
  current: CategoryActivationMaps,
): CategoryActivationWritePlan {
  const { categoryIds, subcategoryIds } = getAllCategoryAndSubcategoryIds();
  const categoryIdsToEnable: string[] = [];
  const subcategoryIdsToEnable: string[] = [];
  const disabledRowsToUpsert: CategoryActivationDisabledRow[] = [];

  for (const id of categoryIds) {
    const wantActive = desired.categories[id] !== false;
    const isActive = current.categories[id] !== false;
    if (wantActive && !isActive) {
      categoryIdsToEnable.push(id);
    } else if (!wantActive && isActive) {
      disabledRowsToUpsert.push({
        entity_type: "category",
        entity_id: id,
        is_active: false,
      });
    }
  }

  for (const id of subcategoryIds) {
    const wantActive = desired.subcategories[id] !== false;
    const isActive = current.subcategories[id] !== false;
    if (wantActive && !isActive) {
      subcategoryIdsToEnable.push(id);
    } else if (!wantActive && isActive) {
      disabledRowsToUpsert.push({
        entity_type: "subcategory",
        entity_id: id,
        is_active: false,
      });
    }
  }

  return {
    categoryIdsToEnable,
    subcategoryIdsToEnable,
    disabledRowsToUpsert,
  };
}

export function isCategoryPlatformActive(
  categoryId: string,
  categories: Record<string, boolean>,
): boolean {
  return categories[categoryId] !== false;
}

export function isSubcategoryPlatformActive(
  subcategoryId: string,
  parentCategoryId: string,
  categories: Record<string, boolean>,
  subcategoryActivation: Record<string, boolean>,
): boolean {
  if (!isCategoryPlatformActive(parentCategoryId, categories)) {
    return false;
  }
  return subcategoryActivation[subcategoryId] !== false;
}

/** Flat active id lists derived from activation maps (parent category must be active). */
export function activeCategoryIdsFromMaps(
  maps: CategoryActivationMaps,
): ActiveCategoryIds {
  const categories = Object.entries(maps.categories)
    .filter(([, active]) => active !== false)
    .map(([id]) => id);

  const subcategoryIds = subcategories
    .filter((sub) =>
      isSubcategoryPlatformActive(
        sub.id,
        sub.parentCategory,
        maps.categories,
        maps.subcategories,
      ),
    )
    .map((sub) => sub.id);

  return { categories, subcategories: subcategoryIds };
}

export function buildActiveCategoryStructure(
  maps: CategoryActivationMaps,
): CategoryStructure[] {
  return categoryStructure
    .filter((category) =>
      isCategoryPlatformActive(category.id, maps.categories),
    )
    .map((category) => ({
      ...category,
      subcategories: category.subcategories.filter((sub) =>
        isSubcategoryPlatformActive(
          sub.id,
          category.id,
          maps.categories,
          maps.subcategories,
        ),
      ),
    }))
    .filter((category) => category.subcategories.length > 0);
}

export function filterSubcategoryIdsToPlatformActive(
  ids: string[],
  active: ActiveCategoryIds,
): string[] {
  const activeCategorySet = new Set(active.categories);
  const activeSubcategorySet = new Set(active.subcategories);

  const next = ids.filter((id) => {
    const parent = SUBCATEGORY_PARENT.get(id);
    if (!parent || !activeCategorySet.has(parent)) return false;
    return activeSubcategorySet.has(id);
  });

  return [...new Set(next)].sort((a, b) => a.localeCompare(b));
}

export function filterSubcategoryIdsToPlatformActiveFromMaps(
  ids: string[],
  maps: CategoryActivationMaps,
): string[] {
  return filterSubcategoryIdsToPlatformActive(
    ids,
    activeCategoryIdsFromMaps(maps),
  );
}

/** Sanitize known subcategory ids, then keep only platform-active ones. */
export function sanitizePlatformSubcategoryAllowlist(
  raw: string[] | null | undefined,
  active: ActiveCategoryIds,
): string[] {
  const sanitized = sanitizeNearMeSubcategories(raw) ?? [];
  return filterSubcategoryIdsToPlatformActive(sanitized, active);
}

/** When a category is turned off, cascade disable all its subcategories in local maps. */
export function applyCategoryDisabledCascade(
  maps: CategoryActivationMaps,
  categoryId: string,
): CategoryActivationMaps {
  const cat = categoryStructure.find((c) => c.id === categoryId);
  if (!cat) return maps;

  const nextSubcategories = { ...maps.subcategories };
  for (const sub of cat.subcategories) {
    nextSubcategories[sub.id] = false;
  }
  return {
    categories: { ...maps.categories, [categoryId]: false },
    subcategories: nextSubcategories,
  };
}

export function setCategoryActivationEnabled(
  maps: CategoryActivationMaps,
  categoryId: string,
  enabled: boolean,
): CategoryActivationMaps {
  if (!enabled) {
    return applyCategoryDisabledCascade(maps, categoryId);
  }
  return {
    ...maps,
    categories: { ...maps.categories, [categoryId]: true },
  };
}

export function setSubcategoryActivationEnabled(
  maps: CategoryActivationMaps,
  subcategoryId: string,
  enabled: boolean,
): CategoryActivationMaps {
  return {
    ...maps,
    subcategories: { ...maps.subcategories, [subcategoryId]: enabled },
  };
}

export async function resolveCategoryActivationMapsFromDatabase(): Promise<CategoryActivationMaps> {
  const serviceSupabase = createServiceSupabaseClient();
  const { data: rows, error } = await serviceSupabase
    .from("category_activation_settings")
    .select("entity_type, entity_id, is_active");

  if (error) {
    console.error("Error fetching category_activation_settings:", error);
    return createDefaultCategoryActivationMaps();
  }

  return buildCategoryActivationMapsFromRows(rows);
}

export async function resolveActiveCategoryIdsFromDatabase(): Promise<ActiveCategoryIds> {
  const maps = await resolveCategoryActivationMapsFromDatabase();
  return activeCategoryIdsFromMaps(maps);
}

export async function fetchCategoryActivationItemCounts(): Promise<CategoryActivationItemCounts> {
  const { categoryIds, subcategoryIds } = getAllCategoryAndSubcategoryIds();
  const byCategory: Record<string, number> = {};
  const bySubcategory: Record<string, number> = {};
  for (const id of categoryIds) byCategory[id] = 0;
  for (const id of subcategoryIds) bySubcategory[id] = 0;

  const serviceSupabase = createServiceSupabaseClient();
  const { data: itemRows, error } = await serviceSupabase
    .from("items")
    .select("category, subcategory");

  if (!error && itemRows) {
    for (const row of itemRows) {
      const cat = row.category ?? null;
      const sub = row.subcategory ?? null;
      if (cat && typeof cat === "string" && cat in byCategory) {
        byCategory[cat] = (byCategory[cat] ?? 0) + 1;
      }
      if (sub && typeof sub === "string" && sub in bySubcategory) {
        bySubcategory[sub] = (bySubcategory[sub] ?? 0) + 1;
      }
    }
  }

  return { byCategory, bySubcategory };
}

export type { SubCategory };
